import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: [ './avatar.component.scss' ],
})
export class AvatarComponent implements OnInit, OnChanges {
  user$!: Observable<any>;
  entity$!: Observable<any>;

  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() firstName = '';
  @Input() lastName = '';
  @Input() email = '';
  @Input() clickableForUpload = false;
  @Input() loginName: string | null = null;
  @Input() inputInitials?: string[];
  @Input() userId?: number; // Add userId input for profile comparison

  initials: string[] = [ 'K', 'S' ];
  logoUrl: string | null = null;
  isLoading = false;
  placeholderColor!: string;
  private reloadLogoSub: Subscription | null = null;
  private isRequestInProgress = false;


  constructor(
    private authService: AuthService,
    private usersService: UsersService,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    this.user$ = this.authService.user$;
    this.entity$ = this.authService.entity$;


    this.user$.subscribe(user => {
      if (!user) return;
      if (!this.firstName) this.firstName = user.firstName;
      if (!this.lastName) this.lastName = user.lastName;
      if (!this.email) this.email = user.contactEmail ?? '';

      this.setPlaceholderColor();
    });
    this.reloadLogoSub = this.usersService.reloadLogo$.subscribe(() => {
      this.loadLogo();
    });
    this.loadLogo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.firstName || this.lastName || this.inputInitials) {
      this.setPlaceholderColor();
    }

    // Only reload logo if loginName or userId actually changed
    const shouldReloadLogo = (changes['loginName'] && changes['loginName'].currentValue !== changes['loginName'].previousValue) ||
      (changes['userId'] && changes['userId'].currentValue !== changes['userId'].previousValue);

    if (shouldReloadLogo && this.loginName) {
      this.loadLogo();
    }
  }

  ngOnDestroy(): void {
    this.reloadLogoSub?.unsubscribe();
  }

  private loadLogo(): void {
    if (!this.loginName) {
      this.logoUrl = null;
      return;
    }

    // Prevent duplicate requests
    if (this.isRequestInProgress) {
      return;
    }

    // Get current logged-in user ID
    const currentUserId = this.entityService.user?.id;

    // If we have a specific userId (profile being viewed), use that user's ID
    // If no userId provided, use current user's ID
    const targetUserId = this.userId || currentUserId;

    // Only load profile picture if we have a valid target user ID
    if (!targetUserId) {
      this.logoUrl = null;
      return;
    }

    this.isLoading = true;
    this.logoUrl = null;
    this.isRequestInProgress = true;

    this.usersService
      .getUserLogo(this.loginName, targetUserId)
      .subscribe({
        next: (response: any) => {
          this.logoUrl = response?.data?.avatarUrl ?? null;
          this.isLoading = false;
          this.isRequestInProgress = false;
        },
        error: (err) => {
          console.error('Failed to load avatar:', err);
          this.isLoading = false;
          this.isRequestInProgress = false;
        },
      });
  }



  getInitials(): string[] {
    if (this.inputInitials && this.inputInitials.length === 2) {
      return this.inputInitials.map(i => i.toUpperCase());
    }
    return [
      this.firstName.charAt(0).toUpperCase(),
      this.lastName.charAt(0).toUpperCase(),
    ];
  }

  private setPlaceholderColor(): void {
    if (!this.firstName?.length || !this.lastName?.length) {
      return;
    }

    const initials = this.getInitials().join('');
    this.placeholderColor = pickColor(initials);
  }
}

const PALETTE = [
  '#2E3B63', // default blue
  '#007D87', // Aqua
  '#A87C36', // Yellow
  '#E25B34', // Orange
  '#BC2B2B', // Red
  '#779F4E', // Green
];

function pickColor(initials: string): string {
  const [ a, b ] = initials.toUpperCase().split('');
  const i1 = a.charCodeAt(0) - 65;
  const i2 = b.charCodeAt(0) - 65;
  const code = i1 * 26 + i2;
  const idx = code % PALETTE.length;
  return PALETTE[idx];
}
